import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import MovieCard from "@/components/feature/MovieCard";
import SectionHeading from "@/components/base/SectionHeading";
import EmptyState from "@/components/base/EmptyState";
import { getRelatedTitles, getTitleById, typeLabel } from "@/utils/content";
import { useFavorites, useRecentlyViewed } from "@/hooks/useLibrary";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const renderStars = (rating: number) => {
  const filled = Math.round(rating / 2);
  return Array.from({ length: 5 }, (_, index) => (
    <i
      key={index}
      className={
        index < filled ? "ri-star-fill text-secondary-400" : "ri-star-line text-background-400/50"
      }
    />
  ));
};

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const item = id ? getTitleById(id) : undefined;
  const { isFavorite, toggleFavorite } = useFavorites();
  const { recordView } = useRecentlyViewed();
  const [toast, setToast] = useState("");

  useDocumentMeta(
    item ? `${item.title} - 午夜影院影视详情` : "资源暂不可用 - 午夜影院",
    item ? `${item.title}（${item.year}，${item.region}）${item.summary.slice(0, 70)}` : undefined
  );

  useEffect(() => {
    if (item) recordView(item.id);
  }, [item, recordView]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2600);
    return () => window.clearTimeout(timer);
  }, [toast]);

  if (!item) {
    return (
      <div className="mx-auto w-full max-w-[1280px] px-4 py-20 md:px-6">
        <EmptyState
          icon="ri-file-unknow-line"
          title="资源暂不可用"
          description="这部作品的资料可能已下架或链接有误。你可以回到首页，或从分类页继续浏览其他内容。"
          actionLabel="返回首页"
          actionTo="/"
        />
      </div>
    );
  }

  const related = getRelatedTitles(item, 4);
  const favorite = isFavorite(item.id);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: `${item.title} - 午夜影院`, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      setToast("页面链接已复制，可以分享给朋友了");
    } catch {
      setToast("复制未完成，你可以手动复制浏览器地址栏");
    }
  };

  return (
    <div className="w-full bg-background-50">
      <section className="relative w-full overflow-hidden bg-primary-950">
        <div className="absolute inset-0">
          <img
            src={item.poster}
            alt=""
            aria-hidden="true"
            className="h-full w-full scale-110 object-cover object-top opacity-25 blur-2xl"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/85 to-primary-950/60" />
        </div>

        <div className="relative mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-12">
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 text-xs text-background-400">
            <Link to="/" className="transition-colors duration-300 hover:text-secondary-300">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <Link
              to={`/category/${item.type}`}
              className="transition-colors duration-300 hover:text-secondary-300"
            >
              {typeLabel(item.type)}分类
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-background-200">{item.title}</span>
          </nav>

          <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-[200px_1fr] md:gap-9 lg:grid-cols-[240px_1fr]">
            <div className="w-40 overflow-hidden rounded-lg bg-background-200 md:w-full">
              <div className="aspect-[2/3] w-full">
                <img
                  src={item.poster}
                  alt={`${item.title}海报`}
                  title={`${item.title} 午夜影院影视详情`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>

            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-sm bg-accent-500 px-2 py-1 text-[11px] font-medium text-primary-950">
                  {typeLabel(item.type)}
                </span>
                {item.genres.map((genre) => (
                  <Link
                    key={genre}
                    to={`/category/${item.type}`}
                    className="rounded-sm border border-background-50/20 px-2 py-1 text-[11px] text-background-200 transition-colors duration-300 hover:border-secondary-400 hover:text-secondary-300"
                  >
                    {genre}
                  </Link>
                ))}
              </div>

              <h1 className="mt-3 font-heading text-3xl font-semibold tracking-tight text-background-50 md:text-4xl">
                {item.title}
              </h1>
              <p className="mt-1.5 font-label text-xs uppercase tracking-[0.16em] text-background-400">
                {item.en}
              </p>

              <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-background-300">
                <span className="font-label">{item.year}</span>
                <span className="text-background-600">|</span>
                <span>{item.region}</span>
                <span className="text-background-600">|</span>
                <span>{item.duration}</span>
                <span className="text-background-600">|</span>
                <span>上映 {item.releaseDate}</span>
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-4">
                <div className="flex items-end gap-2">
                  <span className="font-heading text-4xl font-semibold leading-none text-secondary-300">
                    {item.rating.toFixed(1)}
                  </span>
                  <div>
                    <div className="flex items-center gap-0.5 text-[13px]">{renderStars(item.rating)}</div>
                    <p className="mt-1 text-[11px] text-background-400">站内综合评分</p>
                  </div>
                </div>
                <div className="h-10 w-px bg-background-50/15" />
                <div>
                  <p className="font-label text-sm text-background-100">
                    {item.heat.toLocaleString()}
                  </p>
                  <p className="mt-0.5 text-[11px] text-background-400">近 7 日热度</p>
                </div>
              </div>

              <p className="mt-5 max-w-2xl text-sm leading-7 text-background-200/90">{item.summary}</p>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => toggleFavorite(item.id)}
                  aria-pressed={favorite}
                  className={`flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-medium transition-colors duration-300 ${
                    favorite
                      ? "bg-secondary-500 text-primary-950 hover:bg-secondary-400"
                      : "bg-accent-500 text-primary-950 hover:bg-accent-400"
                  }`}
                >
                  <i className={favorite ? "ri-bookmark-fill" : "ri-bookmark-line"} />
                  {favorite ? "已收藏" : "收藏作品"}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 px-4 py-2.5 text-sm text-background-100 transition-colors duration-300 hover:border-secondary-400 hover:text-secondary-300"
                >
                  <i className="ri-share-forward-line" />
                  分享
                </button>
                <Link
                  to={`/category/${item.type}`}
                  className="flex items-center gap-1 whitespace-nowrap text-xs text-background-400 transition-colors duration-300 hover:text-secondary-300"
                >
                  <i className="ri-arrow-left-line" />
                  返回{typeLabel(item.type)}分类
                </Link>
              </div>

              <p className="mt-4 text-[11px] leading-relaxed text-background-400/80">
                收藏状态保存在本机浏览器中，不涉及登录。本站不提供播放与下载。
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto grid w-full max-w-[1280px] grid-cols-1 gap-10 px-4 py-12 md:px-6 md:py-14 lg:grid-cols-[1fr_300px] lg:gap-12">
        <div>
          <section>
            <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-foreground-950">
              <span className="h-4 w-[3px] rounded-full bg-accent-500" />
              剧情简介
            </h2>
            <div className="mt-4 rounded-lg border border-background-200 bg-background-100/70 p-5">
              <p className="text-sm leading-8 text-foreground-700">{item.summary}</p>
              <p className="mt-3 text-sm leading-8 text-foreground-600">
                影片以{typeLabel(item.type)}的形式呈现，
                {item.genres.join("、")}多重元素交织。适合喜欢
                {item.genres[0]}题材、关注人物刻画的观众观看。
              </p>
            </div>
          </section>

          <section className="mt-10">
            <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-foreground-950">
              <span className="h-4 w-[3px] rounded-full bg-accent-500" />
              演职人员
            </h2>
            <p className="mt-2 text-[11px] text-foreground-400">以下演职人员信息为站内示例资料。</p>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              <div className="rounded-lg border border-background-200 bg-background-50 p-3.5">
                <p className="text-[11px] text-foreground-400">导演</p>
                <p className="mt-1 text-sm font-medium text-foreground-950">{item.director}</p>
              </div>
              {item.cast.map((person) => (
                <div
                  key={person}
                  className="rounded-lg border border-background-200 bg-background-50 p-3.5"
                >
                  <p className="text-[11px] text-foreground-400">主演</p>
                  <p className="mt-1 text-sm font-medium text-foreground-950">{person}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-10">
            <SectionHeading
              kicker="相关推荐"
              title="你可能也喜欢"
              description="依据类型与地区标签匹配，为你挑选相近气质的作品。"
            />
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
              {related.map((entry) => (
                <MovieCard key={entry.id} item={entry} />
              ))}
            </div>
          </section>
        </div>

        <aside className="space-y-4">
          <div className="rounded-lg border border-background-200 bg-background-100 p-5">
            <h2 className="font-heading text-sm font-semibold text-foreground-950">作品资料</h2>
            <dl className="mt-4 space-y-3 text-xs">
              {[
                { label: "作品类型", value: typeLabel(item.type) },
                { label: "制片地区", value: item.region },
                { label: "上映年份", value: String(item.year) },
                { label: "上映日期", value: item.releaseDate },
                { label: "片长 / 集数", value: item.duration },
                { label: "评分", value: `${item.rating.toFixed(1)} 分` },
              ].map((row) => (
                <div key={row.label} className="flex items-start justify-between gap-3">
                  <dt className="shrink-0 text-foreground-500">{row.label}</dt>
                  <dd className="text-right text-foreground-800">{row.value}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-4 flex flex-wrap gap-1.5 border-t border-background-200 pt-4">
              {item.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-sm bg-secondary-100 px-2 py-1 text-[11px] text-secondary-900"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          <div className="rounded-lg bg-primary-900 p-5">
            <p className="flex items-center gap-2 font-label text-[11px] uppercase tracking-[0.18em] text-secondary-400">
              <i className="ri-compass-3-line" />
              分类入口
            </p>
            <p className="mt-2 text-sm leading-relaxed text-background-200">
              这部作品属于「{typeLabel(item.type)}」分类，进入分类页可以继续按地区、年份与评分筛选。
            </p>
            <Link
              to={`/category/${item.type}`}
              className="mt-4 flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-background-50/10 px-3 py-2.5 text-xs text-background-100 transition-colors duration-300 hover:bg-background-50/20"
            >
              浏览{typeLabel(item.type)}分类
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </aside>
      </div>

      {toast ? (
        <div
          role="status"
          className="fixed bottom-8 left-1/2 z-[70] -translate-x-1/2 whitespace-nowrap rounded-md bg-primary-950 px-4 py-2.5 text-xs text-background-100"
        >
          {toast}
        </div>
      ) : null}
    </div>
  );
}